import React, { useMemo, useState } from "react";
import styles from "./Sections.module.scss";
import Spinner from "@src/components/spinner/Spinner";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ScriptableContext,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import moment from "moment";
import { Tooltip } from "antd";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
);

type Props = {
  titleLine1: string;
  titleLine2?: string;
  backGroundUrl?: string;
  isCompleteLesson?: boolean;
  loading?: boolean;
  openCard?: () => void;
  isEducators?: boolean;
  startDate?: string;
  endDate?: string;
  graphData?: any[];
  noWidth?: boolean;
  width?: string;
  isCardBlured?: boolean;
  isSelected?: boolean;
  isClickable?: boolean;
  noData?: boolean;
};

interface TimePart {
  number: string;
  unit: string;
}

function changePointBorderColor(arr) {
  if (arr.length === 0) return arr; // handle empty array
  
  const min = Math.min(...arr);
  const max = Math.max(...arr);
  
  return arr.map(value => value === min || value === max ? 'rgba(255, 176, 115, 1)' : 'transparent');
}

function changePointBorderWidth(arr) {
  if (arr.length === 0) return arr; // handle empty array
  
  const min = Math.min(...arr);
  const max = Math.max(...arr);
  
  return arr.map(value => value === min || value === max ? 8 : 0);
}

function formatTime(time: string) {
  const regex = /(\d+)(yrs|months|days|hrs|hr|mins|secs)/g;
  const parts: TimePart[] = [];
  let match;
  
  // Use regex to match and split numbers and units
  while ((match = regex.exec(time)) !== null) {
    parts.push({ number: match[1], unit: match[2] });
  }
  return parts;
};

function MainCard(props: Props) {
  const {
    titleLine1,
    titleLine2,
    isCompleteLesson,
    loading,
    openCard,
    isEducators,
    startDate,
    endDate,
    graphData,
    noWidth = false,
    width,
    isCardBlured = false,
    isSelected = false,
    isClickable = false,
    noData = false,
  } = props;

  const allZero = useMemo(() => {
    return graphData?.every(value => value === 0)
  }, [graphData])
  const correctGraph = useMemo(() => {
    return allZero && noData ? [10, 60, 30, 70, 120, 100] : graphData?.map(data => data + 10)
  }, [allZero, graphData, noData]) 
  const maxNumber = correctGraph && correctGraph?.length > 0 ? Math.max(...correctGraph) : 300;

  const options = useMemo(() => ({
    maintainAspectRatio: false,
    aspectRatio: 3,
    responsive: true,
    plugins: {
      legend: { display: true },
      datalabels: {
        display: false,
      }
    },
    layout: {
      padding: 0
    },
    scales: {
      x: {
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          display: true,
          color: "#FFB073",
          font: {
            family: 'Montserrat',
            size: 12
          },
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0
        },
      },
      // to remove the y-axis labels
      y: {
        display: false,
        min: 0,
        max: (maxNumber / 90) * 100,
      },
    }
  }), [maxNumber]);

  const formattedStartDate = moment(startDate).format('MMM DD');
  const formattedEndDate = moment(endDate).format('MMM DD');

  const labels = useMemo(() => {
    return correctGraph?.map((data, index) => {
      if (index === 0) {
        return formattedStartDate
      } else if (index === correctGraph.length - 1) {
        return formattedEndDate
      } else {
        return ['']
      }
    })
  }, [correctGraph, formattedStartDate, formattedEndDate])
  const offsetData = useMemo(() => {
    return correctGraph?.map(data => data * 0.9)
  }, [correctGraph])

  const data = useMemo(() => ({
    labels,
    datasets: [
      {
        borderColor: (context: ScriptableContext<"line">) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(200, 0, 0, 200);
          gradient.addColorStop(0, "rgba(255, 176, 115, 0.8)");
          gradient.addColorStop(1, "rgba(255, 176, 115, 0)");
          return gradient;
        },
        pointBorderWidth: graphData ? changePointBorderWidth(graphData) : 1,
        pointBorderColor: graphData ? changePointBorderColor(graphData) : 'transparent',
        pointBackgroundColor: 'transparent',
        borderWidth: 6,
        data: correctGraph,
        fill: false,
        tension: 0.4,
      },
      {
        borderColor: 'transparent',
        pointBorderWidth: 0,
        pointBorderColor: 'transparent',
        borderWidth: 0,
        radius: 0,
        data: offsetData,
        fill: true,
        backgroundColor: (context: ScriptableContext<"line">) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 150);
          gradient.addColorStop(0, "rgba(255, 176, 115, 0.5)");
          gradient.addColorStop(1, "rgba(255, 176, 115, 0)");
          return gradient;
        },
        tension: 0.5,
      },
    ],
    tension: 0.1,
  }), [correctGraph, graphData, labels, offsetData]);

  const timeParts = formatTime(titleLine2 ?? '');
  return (
    <Tooltip
      title={<div className={styles.tooltipText}>Click to view more detailed information</div>}
      placement="top"
      color="#FFFFFF"
    >
    <div
      className={`${styles.mainCard} ${isCardBlured && styles.cardOpacity} ${isSelected && styles.shadowStyle}`}
      onClick={openCard}
      style={{ width: noWidth ? 'auto' : width ? width : '', cursor: isClickable ? 'pointer' : 'default' }}
    >
      {loading ? <div className={styles.spinner}><Spinner /></div> :
      <> 
        <div className={styles.background}>
          {isEducators ? <Line options={options} data={data} width={600} height={180}/> : null}
        </div>
        <div className={styles.cardView}>
          <div className={styles.cardTitle}>
            <span>{titleLine1}</span>
            {timeParts.length > 0 ?
              <div style={{display: "flex", gap: 10}}>
                {timeParts.map((part, index) => (
                  <div className={styles.spentTimeStyle} key={index}>
                    <h2>{part.number}</h2>
                    <span>{part.unit}</span>{' '}
                  </div>
                ))}
              </div> :
              <h2>{titleLine2}</h2>
            }
            {isCompleteLesson ? <p>Completed</p> : null}
          </div>
        </div>
      </>}
    </div>
    </Tooltip>
  );
}

export default MainCard;
